import { useCurrentMethod } from 'contexts/IdPConfigContext';
import { useToast } from 'hooks';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { IScimKeyRequest, IScimKeyResponse } from 'types/api';
import { ToastSeverity } from 'types/toast';
import { post } from './api';
import { API_ROUTES } from './apiRoutes';
import { useMethodSSOId } from './configurations';
import QueryKeys from './queryKeys';

export const useGenerateScimKey = () => {
	const method = useCurrentMethod();
	const { data: methodSSOId } = useMethodSSOId(method);

	const queryClient = useQueryClient();
	const { addToast } = useToast();

	return useCallback(async () => {
		try {
			const res = await post<IScimKeyRequest, IScimKeyResponse>(
				API_ROUTES.scim.key,
				{
					ssoId: methodSSOId || ''
				}
			);
			queryClient.invalidateQueries([QueryKeys.configurations]);
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('scimConfig.key.genereateSuccessMessage')
			});
			return res.data;
		} catch (e) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('scimConfig.key.genereateErrorMessage')
			});
			throw e;
		}
	}, [methodSSOId, queryClient, addToast]);
};

export const useRotateAndRevokeScimKey = () => {
	const method = useCurrentMethod();
	const { data: methodSSOId } = useMethodSSOId(method);

	const queryClient = useQueryClient();
	const { addToast } = useToast();

	return useCallback(async () => {
		try {
			const res = await post<IScimKeyRequest, IScimKeyResponse>(
				API_ROUTES.scim.rotateAndRevoke,
				{
					ssoId: methodSSOId || ''
				}
			);
			queryClient.invalidateQueries([QueryKeys.configurations]);
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('scimConfig.key.rotateAndRevokeSuccessMessage')
			});
			return res.data;
		} catch (e) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('scimConfig.key.rotateAndRevokeErrorMessage')
			});
			throw e;
		}
	}, [methodSSOId, queryClient, addToast]);
};

export const useRevokeScimKey = () => {
	const method = useCurrentMethod();
	const { data: methodSSOId } = useMethodSSOId(method);

	const queryClient = useQueryClient();
	const { addToast } = useToast();

	return useCallback(async () => {
		try {
			await post<IScimKeyRequest, void>(API_ROUTES.scim.revoke, {
				ssoId: methodSSOId || ''
			});
			queryClient.invalidateQueries([QueryKeys.configurations]);
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('scimConfig.key.revokeSuccessMessage')
			});
		} catch (e) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('scimConfig.key.revokeErrorMessage')
			});
			throw e;
		}
	}, [methodSSOId, queryClient, addToast]);
};
